/**
 * OrderDetail
 * --
 */
import React, { useState, useEffect } from 'react';
import { ModalLayout, Title } from 'components';
import {
  Space,
  Tag,
  Spin,
  Select,
  Input,
  Button,
  Descriptions,
  Table,
  Divider,
  InputNumber,
  Flex,
  Radio,
  Avatar,
} from 'antd';
import { ShopOrderApi, ParcelServiceApi, UserApi } from 'api';
import MessageAlert, { stringToMoneyFormat } from 'utils';
import trackDelivery from 'utils/TrackDelivery';
import moment from 'moment';
import { encrypt } from 'utils/Crypto';
import {
  PAYMENT_METHOD_CONSTANT, // 주문 유형
  PAYMENT_STATUS_CONSTANT, // 주문의 결제상태
  PRODUCT_TYPE, // 상품유형
  PACKAGING_METHOD, // 포장방식
  // PRODUCT_STATUS, //상품 판매 상태
  // APPLICATION_STATUS, // 상품 등록 승인여부
  // TAX_TYPE, // 과세유형
  DELIVERY_STATUS,
} from 'constants/ProductConstants';
import { PURCHASE_STATUS } from 'constants/OrderConstants';
import DeliveryCompany from 'constants/DeliveryCompany';
import dayjs from 'dayjs';
import Formatter from 'utils/Formatter';
import { useUpdateShopOrder } from 'hooks/ShopOrderHooks';
import { openAddressModal } from 'utils/DaumAPIManager';
import Alert from 'utils/AlertManager';
import { useUpdateParcelService } from 'hooks/ParcelServiceHooks';

const initialParcelInfo = {
  delivery_company: '',
  invoice_number: '',
};
const REFUND_STATUS = {
  PENDING: {
    label: '환불대기',
    title: '환불대기',
    color: 'volcano',
  },
  APPROVED: {
    label: '환불승인',
    title: '환불승인',
    color: 'green',
  },
  REJECTED: {
    label: '환불반려',
    title: '환불반려',
    color: 'purple',
  },
  COMPLETED: {
    label: '환불완료',
    title: '환불완료',
    color: 'volcano',
  },
};

// 여러 환불 이력이 있는 경우 현재 처리 대상인 최신 환불 정보를 반환합니다.
const getLatestRefund = (refund) => refund?.at(-1);

/**----------------------------------------
 * [Component] 필터
 * ----------------------------------------
 */
const OrderDetail = ({
  isModalVisible,
  orderId,
  onLoaded,
  onUpdateParcel,
  // orderList,
  onCancel,
  type,
  modalType = 'order',
  parcelId,
  onReload,
}) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== Mutate ===== */
  const { updateShopOrder } = useUpdateShopOrder();
  const { updateParcelService } = useUpdateParcelService();

  /* ===== STATE ===== */
  const [loading, setLoading] = useState(false);
  const [orderDetail, setOrderDetail] = useState(isModalVisible);
  // 택배정보 입력 데이터
  const [parcelInfoForm, setParcelInfoForm] = useState(initialParcelInfo);
  /* 환불관련 */
  const [refundModal, setRefundModal] = useState(null);
  const [refundPrice, setRefundPrice] = useState(0);
  const [refundReason, setRefundReason] = useState('');
  const [selectedRefundList, setSelectedRefundList] = useState([]);
  const [recoveryValue, setRecoveryValue] = useState({
    is_coupon_refund: 'Y',
    refund_point: 0,
  });

  const [editMode, setEditMode] = useState(null);
  const [editData, setEditData] = useState({});

  /* ===== VARIABLE ===== */
  // 무통장입금 주문에 처리 중인 환불대기 건이 있는지 확인해 표시 문구에만 사용합니다.
  const isDirectDepositRefundPending =
    orderDetail?.payment_method === 'DIRECT_DEPOSIT' &&
    orderDetail?.parcel_service?.some((parcel) =>
      parcel?.refund?.some((refund) => refund?.refund_status === 'PENDING'),
    );

  /* ===== FUNCTION ===== */
  /**--------------------
   * 주문정보 조회
   * --------------------
   */
  const getOrder = async () => {
    setLoading(true);
    try {
      // 주문정보
      const orderResult = await ShopOrderApi.getShopOrder(orderId);
      // 배송정보
      let parcelResult = await ParcelServiceApi.getParcelServices({
        shop_order_id: orderId,
        join_tables: '["refund"]',
      });
      let allParcelService = [];
      // 유저정보
      const userResult = await UserApi.getUser(orderResult.data.user_id);
      if (modalType === 'shipping') {
        allParcelService = parcelResult?.data;
        parcelResult = parcelResult?.data?.filter(
          (parcel) => parcel?.parcel_service_id === parcelId,
        );
      } else {
        allParcelService = parcelResult?.data;
        parcelResult = parcelResult?.data;
      }
      const data = {
        ...orderResult.data,
        user: userResult.data ?? null,
        // parcel_service: parcelResult.data[0] ?? null,
        parcel_service: parcelResult,
        all_parcel_service: allParcelService,
      };
      setTimeout(() => {
        setParcelInfoForm({
          delivery_company: data?.parcel_service?.[0]?.delivery_company ?? '',
          invoice_number: data?.parcel_service?.[0]?.invoice_number ?? '',
        });
        setLoading(false);
        setOrderDetail(data);
        setEditData({
          ...data.delivery_address,
        });
        onLoaded(data);
      }, 500);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 택배사 정보 저장
   * --------------------
   */
  const handleSaveDeliveryInfo = async () => {
    const { delivery_company, invoice_number } = parcelInfoForm;

    if (!delivery_company) {
      MessageAlert.info('택배사를 선택해주세요.');
      return;
    }

    if (!invoice_number) {
      MessageAlert.info('운송장 번호를 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      const { parcel_service_id } = orderDetail.parcel_service?.[0];

      const d = {
        parcel_service_id,
        delivery_company,
        invoice_number,
      };
      const { status, message } = await ParcelServiceApi.updateParcelService(d);

      if (status !== 200) throw { message };

      onReload && onReload();

      setTimeout(() => {
        MessageAlert.success('택배정보가 저장되었습니다.');
        onUpdateParcel(d);
        setLoading(false);
      }, 350);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 배송상태 변경
   * --------------------
   */
  const handleChangeDeliveryStatus = async (newValue) => {
    const { title } = DELIVERY_STATUS[newValue];
    const { parcel_service_id, delivery_company, invoice_number } =
      orderDetail.parcel_service?.[0];

    // 택배/운송장 정보가 없을 경우 처리 불가 예외처리
    if (!delivery_company || !invoice_number) {
      return MessageAlert.warning('택배/운송장 정보를 먼저 등록해주세요');
    }
    // 상태변경 확인 컨펌
    if (!window.confirm(`배송상태를 [${title}]로 변경하시겠습니까?`)) {
      return;
    }

    setLoading(true);
    try {
      // 입력데이터
      const newData = [
        {
          parcel_service_id,
          invoice_number,
          delivery_company,
          regular_payment_id: null, // null 인경우 일반상품
          delivery_status: newValue,
          ...(newValue === 'DELIVERED' && { delivery_date: moment().unix() }), // 배송중으로 바꿀땐 전송 X
        },
      ];

      // API
      const { status, message } =
        await ParcelServiceApi.updateParcelServiceStatusBulk(newData);
      if (status !== 200) throw { message };

      setLoading(false);
      // 기존데이터 수정
      setOrderDetail((prev) => ({
        ...prev,
        parcel_service: prev.parcel_service?.map((item) => ({
          ...item,
          delivery_status: newValue,
        })),
      }));

      onReload && onReload();

      MessageAlert.info(`배송상테가 [${title}]로 변경되었습니다.`);
    } catch (e) {
      setLoading(false);
      console.log('[OrderDetail][handleChangeDeliveryStatus] Error: ', e);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 환불
   * --------------------
   */
  const handleRefund = async () => {
    setLoading(true);
    try {
      // 예외처리
      // 분류 : 타입에 따라서
      // const { type } = orderDetail;
      // const refundList = selectedRefundList.map((item) => ({
      //   type: item.type,
      //   data: {
      //     parcel_service_id: item.parcel_service_id, // 주문아이디
      //     ...(item.type === 'SUBSCRIPTION' && {
      //       regular_payment_id: item.regular_payment_info[0].regular_payment_id,
      //     }),
      //     refund_amount: item.payment_price, // 결제금액
      //     refund_num: item.num, // 수량
      //     refund_status: 'COMPLETED', // 상태
      //     refund_reason: refundReason, // 사유
      //     refund_date: moment().unix(), // 취소시간
      //     refund_data:
      //       item.coupon_info && item.coupon_info.length > 0
      //         ? item.coupon_info
      //         : null,
      //     refund_payment_info: null, // 실제환불 API연결 시 응답받은 payment_info 입력
      //     is_coupon_refund:
      //       item.coupon_info && item.coupon_info.length > 0 ? 'Y' : 'N',
      //     // refund_point, // 취소할 포인트
      //   },
      // }));

      //  // API
      //  refundList.forEach(async (item) => {
      //   const { status } = await RefundApi.createRefundManual(
      //     item.type,
      //     item.data
      //   );
      //   if (status !== 200) MessageAlesrt.warning(`주문을 취소할 수 없습니다.`);
      // });

      const { payment_info, parcel_service, payment_method } = orderDetail;
      const cancelSeq = parcel_service.reduce((acc, { refund }) => {
        return refund.length ? acc + 1 : acc;
      }, 1);
      const refundList = selectedRefundList.map((item) => {
        const {
          product,
          parcel_service_id,
          type,
          regular_payment_info,
          rental_payment_info,
          user_id,
          refund,
          card_id,
        } = item;
        // 환불대기 이력이 있으면 해당 건을 완료 처리 대상으로 전달합니다.
        const pendingRefund = refund?.find(
          (item) => item?.refund_status === 'PENDING',
        );
        const refundId =
          pendingRefund?.refund_id ??
          getLatestRefund(refund)?.refund_id ??
          null;
        let trno = payment_info?.trno || payment_info?.tid;
        let regularPaymentId = null;
        let rentalPaymentId = null;
        //NOTE: 정기배송
        if (type === 'SUBSCRIPTION') {
          const regularPaymentInfo = regular_payment_info.at(-1);
          regularPaymentId = regularPaymentInfo?.regular_payment_id;
          trno =
            regularPaymentInfo.payment_info?.trno ||
            regularPaymentInfo.payment_info?.tid;
          //NOTE: 렌탈
        } else if (type === 'RENTAL') {
          const rentalPaymentInfo = rental_payment_info.at(-1);
          rentalPaymentId = rentalPaymentInfo?.rental_payment_id;
          trno =
            rentalPaymentInfo?.payment_info?.trno ||
            rentalPaymentInfo?.payment_info?.tid;
        }

        // const trno = 1
        // const regular_payment_id = 1
        return {
          trno: trno,
          cancelTaxFreeAmount: product.tax_free_amount,
          cancelSeq: cancelSeq,
          parcel_service_id,
          product_type: type,
          refund_reason: refundReason,
          refund_amount: refundPrice,
          refund_num: product.num,
          is_coupon_refund: recoveryValue.is_coupon_refund,
          refund_point: recoveryValue.refund_point || 0,
          user_id: user_id,
          refund_id: refundId,
          regular_payment_id: regularPaymentId,
          rental_payment_id: rentalPaymentId,
          card_id,
        };
      });

      let isRefundCompleted = false;

      for (const item of refundList) {
        const res = await onCancel(item, payment_method);

        if (res) {
          isRefundCompleted = true;
        }
      }

      // 주문취소 성공 시 상세정보와 부모 주문 목록을 함께 최신화합니다.
      if (isRefundCompleted) {
        await getOrder();
        await onReload?.();
        setRefundModal(null);
      }

      setSelectedRefundList([]);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };
  // console.log('selectedRefundList', selectedRefundList);
  // console.log('refundModal', refundModal);
  /**--------------------
   * 입금 상태 변경
   * --------------------
   */
  const handleChangeDepositStatus = async () => {
    const confirm = window.confirm(
      `입금상태를 "입금확인" 상태로 변경하시겠습니까?`,
    );

    if (!confirm) return;

    try {
      setLoading(true);
      const parcelData = orderDetail?.parcel_service?.filter(
        (item) => item.deposit_status === 'PENDING',
      );
      const shopOrderData = { ...orderDetail, payment_state: 'COMPLETED' };
      if (!parcelData.length) {
        MessageAlert.info('처리 건이 없습니다.');
        return;
      }

      const updateData = parcelData.map((item) => ({
        parcel_service_id: item?.parcel_service_id,
        deposit_status: 'COMPLETED',
      }));

      const { status } =
        await ParcelServiceApi.bulkUpdateParcelService(updateData);
      const shopOrderUpdate = await updateShopOrder(shopOrderData);
      if (status !== 200) {
        MessageAlert.error('입금상태 변경에 실패하였습니다.');
        return;
      }
      if (shopOrderUpdate.status !== 200) {
        MessageAlert.error('주문 정보 수정에 실패하였습니다.');
        return;
      }

      MessageAlert.success('변경되었습니다.');
      getOrder();
    } catch (err) {
      console.error('[handleChangeDepositStatus] Error!! ', err);
      MessageAlert.error('입금상태 변경에 실패하였습니다.');
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  };

  /**
   * 수령지 값 변경
   * --
   */
  const handleChange = (t, v) => {
    setEditData((prev) => ({
      ...prev,
      [t]: v,
    }));
  };

  /**--------------------
   * 다음 주소 검색 Popup Open
   * --------------------
   */
  const handleOpenAddressModal = (type) => {
    const key1 =
      type === 'delivery' ? 'recipient_postcode' : 'forwarding_postcode';
    const key2 =
      type === 'delivery' ? 'recipient_address' : 'forwarding_address';

    openAddressModal((res) => {
      handleChange(key1, res.zonecode);
      handleChange(key2, res.address);
    });
  };

  /**
   *
   * @param {string} t
   * @param {any} d
   */
  const handleEditSubmit = async (t, d) => {
    if (!Alert.confirm('주문 정보를 수정하시겠습니까?')) return;

    try {
      let func = null;
      let data = null;

      if (t === 'purchase_status') {
        const { parcel_service_id, data: updateData } = d;

        func = updateParcelService;
        data = {
          parcel_service_id,
          [t]: updateData,
        };
      } else {
        func = updateShopOrder;
        data = {
          shop_order_id: orderId,
          [t]: d,
        };
      }

      const { status, message } = await func(data);

      if (status !== 200) {
        MessageAlert.warning('주문 정보 변경에 실패하였습니다.');
        console.warn('[handleEditSubmit] Fail : ', message);
      }

      getOrder();

      onReload && onReload();
      MessageAlert.success('주문 정보가 변경되었습니다.');
      setEditMode(false);
    } catch (err) {
      console.error('handleEditSubmit', err);
      MessageAlert.error('정보 변경 실패!');
    }
  };

  /* ===== HOOKS ===== */
  useEffect(() => {
    orderId && getOrder();
  }, [orderId]);

  const getNumber = (value) => Number(value ?? 0);

  const getUsePointParcelList = () => {
    if (Array.isArray(orderDetail?.all_parcel_service)) {
      return orderDetail.all_parcel_service;
    }

    return Array.isArray(orderDetail?.parcel_service)
      ? orderDetail.parcel_service
      : [];
  };

  const getParcelUsePoint = (parcel) =>
    getNumber(parcel?.product?.use_point ?? parcel?.use_point);

  const getDisplayUsePoint = (record, index = 0) => {
    const orderUsePoint = getNumber(orderDetail?.use_point);
    const parcelList = getUsePointParcelList();
    const parcelUsePointTotal = parcelList.reduce(
      (acc, parcel) => acc + getParcelUsePoint(parcel),
      0,
    );

    if (!orderUsePoint) {
      return getParcelUsePoint(record);
    }

    if (parcelUsePointTotal === orderUsePoint) {
      return getParcelUsePoint(record);
    }

    return index === 0 ? orderUsePoint : 0;
  };

  /* ===== RENDER ===== */
  // console.log('orderDetail', orderDetail?.parcel_service);
  // JSX: start
  return (
    <>
      <Spin spinning={loading}>
        {orderDetail && (
          <div>
            {/* ===== 상품정보: start ===== */}

            <Flex justify="space-between" align="center">
              <Title size={5}>
                주문내역(
                {modalType === 'order'
                  ? orderDetail?.product_information.length
                  : orderDetail?.parcel_service.length}
                )
              </Title>
              {editMode === 'PURCHASE' ? (
                <Flex gap={4}>
                  <Button size="small" onClick={() => setEditMode(null)}>
                    취소
                  </Button>
                </Flex>
              ) : (
                <Button
                  size="small"
                  type="primary"
                  onClick={() => setEditMode('PURCHASE')}
                >
                  수정
                </Button>
              )}
            </Flex>
            <div style={{ maxHeight: 420, overflowY: 'scroll' }}>
              <Table
                size="small"
                dataSource={orderDetail?.parcel_service}
                pagination={false}
                scroll={{ y: 680, x: 800 }} // 테이블 자체 스크롤 설정
                style={{
                  marginTop: 5,
                  borderTop: '2px solid #d1d1d1',
                  borderRadius: 5,
                }}
                // isChecked
                bordered
                columns={[
                  {
                    title: '품목별주문번호',
                    key: 'parcel_service_id',
                    dataIndex: 'parcel_service_id',
                    width: 135,
                    render: (item) => item,
                  },
                  {
                    title: '주문상태',
                    key: 'purchase_status',
                    dataIndex: 'purchase_status',
                    width: 130,
                    render: (item, record) =>
                      editMode === 'PURCHASE' ? (
                        <Flex gap={4} align="center" justify="space-between">
                          <Select
                            style={{ flex: 1 }}
                            value={item}
                            options={Object.entries(PURCHASE_STATUS).map(
                              ([key, value]) => ({
                                label: value?.title,
                                value: key,
                              }),
                            )}
                            onChange={(e) =>
                              handleEditSubmit('purchase_status', {
                                parcel_service_id: record?.parcel_service_id,
                                data: e,
                              })
                            }
                          />
                        </Flex>
                      ) : (
                        <Tag color={PURCHASE_STATUS[item]?.color}>
                          {PURCHASE_STATUS[item]?.title}
                        </Tag>
                      ),
                  },
                  {
                    title: '상품코드',
                    key: 'product_code',
                    dataIndex: 'product',
                    width: 135,
                    render: (item) => item?.product_code,
                  },
                  {
                    title: '상품명',
                    key: 'product_name',
                    dataIndex: 'product',
                    width: 300,
                    render: (v, record) => (
                      <Flex gap={8}>
                        <Avatar src={v?.thumbnail} size={64} shape="square" />
                        <div>
                          <a
                            href={`/#/product/list/${v?.product_id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              window.open(
                                e.currentTarget.href,
                                '_blank',
                                'width=860, height=620',
                              );
                            }}
                          >
                            {v?.product_name}
                          </a>
                          <p
                            style={{
                              fontSize: '0.75em',
                              fontWeight: '500',
                              marginTop: 3.5,
                              opacity: 0.5,
                            }}
                          >
                            옵션목록({v?.product_option?.length})
                          </p>
                          <ul
                            style={{
                              fontSize: '0.9em',
                              color: '#FFFFFF', // 글자 색상
                              backgroundColor: v?.product_option?.length
                                ? '#671A31'
                                : null, // 배경 색상
                              padding: '10px', // 필요하면 패딩 추가
                              borderRadius: '6px', // 모서리 둥글게
                            }}
                          >
                            {v?.product_option?.map((item) => (
                              <li>
                                - {item.option_title} /{' '}
                                {item?.option?.map((option) => (
                                  <>{option?.option_name || '-'}</>
                                ))}{' '}
                                /{' '}
                                {stringToMoneyFormat(
                                  item?.option?.reduce(
                                    (acc, cur) => acc + (cur.option_price || 0),
                                    0,
                                  ),
                                  '원',
                                )}
                                / {v?.num}
                                {'개'}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </Flex>
                    ),
                  },
                  {
                    title: '수량',
                    key: 'num',
                    dataIndex: 'num',
                    width: 70,
                    render: (v) => stringToMoneyFormat(v),
                  },
                  {
                    title: '단가',
                    key: 'price',
                    dataIndex: 'product',
                    width: 100,
                    render: (v) => stringToMoneyFormat(v?.price),
                  },
                  {
                    title: '옵션금액',
                    key: 'option_price',
                    dataIndex: 'product',
                    width: 100,
                    render: (val) =>
                      stringToMoneyFormat(
                        val?.product_option?.reduce(
                          (acc, cur) =>
                            acc +
                            cur?.option?.reduce(
                              (acc, cur) => acc + (cur?.option_price || 0),
                              0,
                            ),
                          0,
                        ) * val?.num,
                      ),
                  },
                  {
                    title: '합계금액',
                    key: 'price',
                    dataIndex: 'product',
                    width: 100,
                    render: (val) =>
                      stringToMoneyFormat(
                        val?.price * val.num +
                          val?.product_option?.reduce(
                            (acc, cur) =>
                              acc +
                              cur?.option?.reduce(
                                (acc, cur) => acc + (cur?.option_price || 0),
                                0,
                              ),
                            0,
                          ) *
                            val?.num,
                      ),
                  },
                  {
                    title: '할인금',
                    key: 'discount_price',
                    dataIndex: 'discount_price',
                    width: 100,
                    render: (val, record, index) =>
                      stringToMoneyFormat(
                        getNumber(val) - getDisplayUsePoint(record, index),
                      ),
                  },
                  {
                    title: '쿠폰할인',
                    key: 'cupon_discount_price',
                    dataIndex: 'product',
                    width: 100,
                    render: (val, record) =>
                      stringToMoneyFormat(
                        -(
                          val?.coupon_discount_price ??
                          record?.coupon_discount_price ??
                          0
                        ),
                      ),
                  },
                  {
                    title: '등급할인',
                    key: 'grade_discount_price',
                    dataIndex: 'product',
                    width: 100,
                    render: (val, record) =>
                      stringToMoneyFormat(
                        -(
                          val?.grade_discount_price ??
                          record?.grade_discount_price ??
                          0
                        ),
                      ),
                  },
                  {
                    title: '프로모션할인',
                    key: 'promotion_discount_price',
                    dataIndex: 'product',
                    width: 100,
                    render: (val, record) =>
                      stringToMoneyFormat(
                        -(
                          val?.promotion_discount_price ??
                          record?.promotion_discount_price ??
                          0
                        ),
                      ),
                  },
                  {
                    title: '사용포인트',
                    key: 'use_point',
                    dataIndex: 'product',
                    width: 100,
                    render: (val, record, index) =>
                      stringToMoneyFormat(getDisplayUsePoint(record, index)),
                  },
                  {
                    title: '결제금액',
                    key: 'payment_price',
                    dataIndex: 'payment_price',
                    width: 100,
                    render: (payment_price) =>
                      stringToMoneyFormat(payment_price),
                  },
                  {
                    title: '포장방식',
                    key: 'packaging_method',
                    dataIndex: 'product',
                    width: 100,
                    render: (v) => PACKAGING_METHOD[v?.packaging_method].title,
                  },
                  {
                    title: '판매자',
                    key: 'shop_information_name',
                    dataIndex: 'product',
                    width: 200,
                    render: (v, record) => v?.shop_information_name,
                  },

                  // {
                  //   title: '포장방식',
                  //   key: 'packaging_method',
                  //   dataIndex: 'packaging_method',
                  //   // width: '17.5%',
                  //   colSpan: 8,
                  //   render: (v) => PACKAGING_METHOD[v].title,
                  // },
                ]}
              />
            </div>
            {/* ===== 상품정보: end ===== */}
            <br />
            <br />
            {/* ===== 주문&배송정보: start ===== */}
            <Flex justify="space-between" align="center">
              <Title size={5}>주문&배송정보</Title>
              {editMode === 'DELIVERY' ? (
                <Flex gap={4}>
                  <Button size="small" onClick={() => setEditMode(null)}>
                    취소
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() =>
                      handleEditSubmit('delivery_address', editData)
                    }
                  >
                    완료
                  </Button>
                </Flex>
              ) : (
                <Button
                  size="small"
                  type="primary"
                  onClick={() => setEditMode('DELIVERY')}
                >
                  수정
                </Button>
              )}
            </Flex>
            <Descriptions
              size="small"
              bordered
              column={2}
              labelStyle={{
                width: 125,
                color: '#111',
                fontWeight: '500',
                background: '#f4f4f4',
              }}
              style={{
                borderTop: '2px solid #d1d1d1',
                borderRadius: 5,
                marginTop: 5,
              }}
              items={[
                /* === Line 1 === */
                {
                  key: '주문자',
                  label: '주문자',
                  children: (
                    <span>
                      <a
                        href={`/#/users/list?modal=${encodeURIComponent(
                          encrypt(orderDetail?.user?.user_id),
                        )}`}
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(
                            e.currentTarget.href,
                            '_blank',
                            'width=860, height=620',
                          );
                        }}
                      >
                        {orderDetail?.user?.user_name}
                      </a>
                    </span>
                  ),
                },
                {
                  key: 'user_phone',
                  label: '주문자 연락처',
                  children: <span>{orderDetail?.user?.user_phone}</span>,
                },

                /* === Line 2 === */
                ...(orderDetail?.type !== 'STORE_PICKUP'
                  ? [
                      {
                        key: 'recipient_name',
                        label: '수령인',
                        children:
                          editMode === 'DELIVERY' ? (
                            <Input
                              size="small"
                              value={editData.recipient_name}
                              onChange={(e) =>
                                handleChange('recipient_name', e.target.value)
                              }
                            />
                          ) : (
                            <span>
                              {orderDetail?.delivery_address?.recipient_name}
                            </span>
                          ),
                      },
                      {
                        key: 'recipient_phone',
                        label: '수령인 연락처',
                        children:
                          editMode === 'DELIVERY' ? (
                            <Input
                              size="small"
                              value={editData.recipient_phone}
                              onChange={(e) =>
                                handleChange(
                                  'recipient_phone',
                                  Formatter.hyphen(e.target.value),
                                )
                              }
                            />
                          ) : (
                            <span>
                              {orderDetail?.delivery_address?.recipient_phone}
                            </span>
                          ),
                      },
                    ]
                  : []),

                /* === Line 3 === */
                {
                  key: 'delivery_address.request',
                  label: '배송요청사항',
                  span: 2,
                  children:
                    editMode === 'DELIVERY' ? (
                      <Input
                        size="small"
                        value={editData.request}
                        onChange={(e) =>
                          handleChange('request', e.target.value)
                        }
                      />
                    ) : (
                      <span>{orderDetail?.delivery_address?.request}</span>
                    ),
                },

                /* === Line 4 === */
                ...(orderDetail?.type !== 'STORE_PICKUP'
                  ? [
                      {
                        key: 'recipient_address',
                        label: '주소(상세)',
                        span: 2,
                        children:
                          editMode === 'DELIVERY' ? (
                            <Flex vertical gap={4}>
                              <Flex gap={4} align="center">
                                <Input
                                  size="small"
                                  value={`[${editData.recipient_postcode}] ${editData.recipient_address}`}
                                  readOnly
                                />
                                <Button
                                  size="small"
                                  onClick={() =>
                                    handleOpenAddressModal('delivery')
                                  }
                                >
                                  주소 검색
                                </Button>
                              </Flex>
                              <Input
                                size="small"
                                value={editData.recipient_address_detail}
                                onChange={(e) =>
                                  handleChange(
                                    'recipient_address_detail',
                                    e.target.value,
                                  )
                                }
                              />
                            </Flex>
                          ) : (
                            <span>
                              [
                              {
                                orderDetail?.delivery_address
                                  ?.recipient_postcode
                              }
                              ]{' '}
                              {orderDetail?.delivery_address?.recipient_address}
                              (
                              {
                                orderDetail?.delivery_address
                                  ?.recipient_address_detail
                              }
                              )
                            </span>
                          ),
                      },
                    ]
                  : [
                      {
                        key: 'recipient_address',
                        label: '픽업지',
                        span: 2,
                        children:
                          editMode === 'DELIVERY' ? (
                            <Flex vertical gap={4}>
                              <Flex gap={4} align="center">
                                <Input
                                  size="small"
                                  value={`[${editData.forwarding_postcode}] ${editData.forwarding_address}`}
                                  readOnly
                                />
                                <Button
                                  size="small"
                                  onClick={() =>
                                    handleOpenAddressModal('pickup')
                                  }
                                >
                                  주소 검색
                                </Button>
                              </Flex>
                              <Input
                                size="small"
                                value={editData.forwarding_address_detail}
                                onChange={(e) =>
                                  handleChange(
                                    'forwarding_address_detail',
                                    e.target.value,
                                  )
                                }
                              />
                            </Flex>
                          ) : (
                            <span>
                              [
                              {
                                orderDetail?.delivery_address
                                  ?.forwarding_postcode
                              }
                              ]{' '}
                              {
                                orderDetail?.delivery_address
                                  ?.forwarding_address
                              }
                              (
                              {
                                orderDetail?.delivery_address
                                  ?.forwarding_address_detail
                              }
                              )
                            </span>
                          ),
                      },
                    ]),
                ...(orderDetail?.type !== 'STORE_PICKUP' &&
                modalType !== 'order'
                  ? [
                      /* === Line 5 === */
                      {
                        key: 'delivery_company',
                        label: '택배사/운송장',
                        span: 2,
                        children: (
                          <Space.Compact style={{ width: '100%' }}>
                            <Select
                              style={{ width: 150 }}
                              placeholder="택배사"
                              options={Object.keys(DeliveryCompany).map(
                                (item) => ({
                                  value: DeliveryCompany[item].value,
                                  label: DeliveryCompany[item].label,
                                }),
                              )}
                              value={parcelInfoForm?.delivery_company}
                              onChange={(e) =>
                                setParcelInfoForm((p) => ({
                                  ...p,
                                  delivery_company: e,
                                }))
                              }
                            />
                            <Input
                              placeholder="송장번호를 입력해주세요"
                              style={{ width: 'calc(100% - 230px)' }}
                              value={parcelInfoForm.invoice_number}
                              onChange={(e) =>
                                setParcelInfoForm((p) => ({
                                  ...p,
                                  invoice_number: e.target.value,
                                }))
                              }
                            />
                            <Button
                              type="primary"
                              style={{ width: 80 }}
                              onClick={handleSaveDeliveryInfo}
                            >
                              저장
                            </Button>
                          </Space.Compact>
                        ),
                      },
                      /* === Line 6 === */
                      {
                        key: 'delivery_status',
                        label: '배송상태',
                        children: (
                          <>
                            <Select
                              style={{ width: '100%' }}
                              placeholder="배송상태"
                              options={Object.keys(DELIVERY_STATUS).map(
                                (item) => ({
                                  value: DELIVERY_STATUS[item].value,
                                  label: DELIVERY_STATUS[item].title,
                                }),
                              )}
                              value={
                                orderDetail?.parcel_service?.[0]
                                  ?.delivery_status
                              }
                              onChange={handleChangeDeliveryStatus}
                            />
                          </>
                        ),
                      },
                      {
                        key: 'recipient_address_detail',
                        label: '배송조회',
                        children: (
                          <Button
                            block
                            disabled={
                              !parcelInfoForm.invoice_number ? true : false
                            }
                            onClick={() =>
                              trackDelivery(
                                parcelInfoForm.delivery_company,
                                parcelInfoForm.invoice_number,
                              )
                            }
                          >
                            배송조회
                          </Button>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
            {/* ===== 주문&배송정보: end ===== */}

            <br />
            <br />
            {/* ===== 결제정보: start ===== */}
            <Title size={5}>결제정보</Title>
            <Descriptions
              column={2}
              size="small"
              bordered
              labelStyle={{
                width: 125,
                color: '#111',
                fontWeight: '500',
                background: '#f4f4f4',
              }}
              style={{
                borderTop: '2px solid #d1d1d1',
                borderRadius: 5,
                marginTop: 5,
              }}
              items={[
                /* === Line 1 === */
                {
                  key: 'shop_order_id',
                  label: '주문번호',
                  children: orderDetail?.shop_order_id,
                },
                {
                  key: 'payment_serial_number',
                  label: '결제시리얼',
                  children: (
                    <span style={{ letterSpacing: -0.7, fontSize: '0.85em' }}>
                      {orderDetail?.payment_serial_number}
                    </span>
                  ),
                },

                /* === Line 2 === */
                {
                  key: '2',
                  label: '주문일(시간)',
                  children: (
                    <p>
                      {moment(orderDetail?.created_at * 1000).format(
                        'YYYY-MM-DD (HH:mm:ss)',
                      )}
                    </p>
                  ),
                },
                {
                  key: '3',
                  label: '결제(결제수단)',
                  children:
                    editMode === 'PAYMENT' ? (
                      <Flex gap={4} align="center" justify="space-between">
                        <Select
                          style={{ flex: 1 }}
                          value={orderDetail?.payment_state}
                          options={Object.entries(PAYMENT_STATUS_CONSTANT)
                            .filter(([key]) => key !== 'REFUNDED')
                            .map(([key, value]) => ({
                              label: value?.title,
                              value: key,
                            }))}
                          onChange={(e) => handleEditSubmit('payment_state', e)}
                        />
                        <Button size="small" onClick={() => setEditMode(null)}>
                          취소
                        </Button>
                      </Flex>
                    ) : (
                      <Flex gap={4} align="center" justify="space-between">
                        <Tag
                          color={
                            isDirectDepositRefundPending
                              ? 'orange'
                              : PAYMENT_STATUS_CONSTANT[
                                  orderDetail?.payment_state
                                ]?.color
                          }
                        >
                          {isDirectDepositRefundPending
                            ? '환불대기'
                            : PAYMENT_STATUS_CONSTANT[
                                orderDetail?.payment_state
                              ]?.title}
                          (
                          {
                            PAYMENT_METHOD_CONSTANT[orderDetail?.payment_method]
                              ?.title
                          }
                          )
                        </Tag>
                        <Button
                          size="small"
                          type="primary"
                          onClick={() => setEditMode('PAYMENT')}
                        >
                          수정
                        </Button>
                      </Flex>
                    ),
                },

                /* === Line 3 === */
                {
                  key: '5',
                  label: '상품유형',
                  children: <p>{PRODUCT_TYPE[orderDetail?.type]?.title}</p>,
                },
                {
                  key: '4',
                  label: '배송비',
                  children: (
                    <p>
                      {stringToMoneyFormat(orderDetail?.final_delivery_price)}원
                    </p>
                  ),
                },
                {
                  key: '10',
                  label: '쿠폰/할인',
                  children: (
                    <p>{stringToMoneyFormat(orderDetail?.final_discount)}원</p>
                  ),
                },
                {
                  key: '11',
                  label: '사용포인트',
                  children: (
                    <p>{stringToMoneyFormat(orderDetail?.use_point)}원</p>
                  ),
                },

                /* === Line 4 === */
                {
                  key: '4',
                  label: '총결제금액',
                  children: (
                    <p>{stringToMoneyFormat(orderDetail?.final_price)}원</p>
                  ),
                  span: 2,
                },
                // {
                //   key: 'purchase_status',
                //   label: '주문상태',
                //   children: editMode === 'PURCHASE' ? (<Flex gap={4} align="center" justify="space-between">
                //     <Select
                //       style={{ flex: 1 }}
                //       value={orderDetail?.payment_state}
                //       options={Object.entries(PURCHASE_STATUS).map(
                //         ([key, value]) => ({
                //           label: value?.title,
                //           value: key,
                //         })
                //       )}
                //       onChange={(e) => handleEditSubmit('payment_state', e)}
                //     />
                //     <Button size="small" onClick={() => setEditMode(null)}>
                //       취소
                //     </Button>
                //   </Flex>) : (
                //     <Tag
                //       color={
                //         PURCHASE_STATUS[
                //           orderDetail?.parcel_service?.[0]?.purchase_status
                //         ]?.color
                //       }
                //     >
                //       {
                //         PURCHASE_STATUS[
                //           orderDetail?.parcel_service?.[0]?.purchase_status
                //         ]?.title
                //       }
                //     </Tag>
                //   ),
                // },

                /* === Line 5 === */
                ...(orderDetail?.payment_info &&
                orderDetail?.payment_method !== 'JM_PAY'
                  ? [
                      {
                        key: 'payment_info',
                        label: '결제정보',
                        children:
                          orderDetail?.payment_method === 'DIRECT_DEPOSIT' ? (
                            <Flex vertical gap={4}>
                              <Flex>
                                <p style={{ flex: 0.1, fontWeight: 700 }}>
                                  입금자명:
                                </p>
                                <p style={{ flex: 0.9 }}>
                                  {orderDetail?.payment_info?.deposit_name ??
                                    '-'}
                                </p>
                              </Flex>
                              <Flex>
                                <p style={{ flex: 0.1, fontWeight: 700 }}>
                                  현금영수증:
                                </p>
                                <p style={{ flex: 0.9 }}>
                                  {orderDetail?.payment_info?.cash_receipt ===
                                  'Y'
                                    ? '신청'
                                    : '미신청'}
                                </p>
                              </Flex>
                              {orderDetail?.payment_info?.cash_receipt ===
                                'Y' && (
                                <Flex vertical gap={4}>
                                  <Flex>
                                    <p style={{ flex: 0.15, fontWeight: 700 }}>
                                      현금영수증 구분:
                                    </p>
                                    <p style={{ flex: 0.9 }}>
                                      {orderDetail?.payment_info
                                        ?.receipt_type === 'company'
                                        ? '사업자'
                                        : orderDetail?.payment_info
                                              ?.receipt_type === 'solo'
                                          ? '개인'
                                          : '-'}
                                    </p>
                                  </Flex>
                                  <Flex>
                                    <p style={{ flex: 0.1, fontWeight: 700 }}>
                                      {`${
                                        orderDetail?.payment_info
                                          ?.receipt_type === 'company'
                                          ? '사업자번호:'
                                          : '전화번호:'
                                      }`}
                                    </p>
                                    <p style={{ flex: 0.9 }}>
                                      {orderDetail?.payment_info?.deposit_phone}
                                    </p>
                                  </Flex>
                                </Flex>
                              )}
                            </Flex>
                          ) : (
                            <Flex vertical gap={4}>
                              <Flex>
                                <p style={{ flex: 0.1, fontWeight: 700 }}>
                                  결제승인:
                                </p>
                                <p style={{ flex: 0.9 }}>
                                  {orderDetail?.payment_info?.authyn ??
                                    orderDetail?.payment_info?.respCode}
                                </p>
                              </Flex>
                              <Flex>
                                <p style={{ flex: 0.1, fontWeight: 700 }}>
                                  승인번호:
                                </p>
                                <p style={{ flex: 0.9 }}>
                                  {orderDetail?.payment_info?.trno ??
                                    orderDetail?.payment_info?.tid}
                                </p>
                              </Flex>
                              <Flex>
                                <p style={{ flex: 0.1, fontWeight: 700 }}>
                                  승인키:
                                </p>
                                <p style={{ flex: 0.9 }}>
                                  {orderDetail?.payment_info?.ordno ??
                                    orderDetail?.payment_info?.approvalNumb}
                                </p>
                              </Flex>
                              <Flex>
                                <p style={{ flex: 0.1, fontWeight: 700 }}>
                                  결제일:
                                </p>
                                <p style={{ flex: 0.9 }}>
                                  {orderDetail?.payment_info?.trddt
                                    ? `${orderDetail?.payment_info?.trddt} ${orderDetail?.payment_info?.trdtm}`
                                    : orderDetail?.payment_info?.tradeDateTime}
                                </p>
                              </Flex>
                              <Flex>
                                <p style={{ flex: 0.1, fontWeight: 700 }}>
                                  결제금액:
                                </p>
                                <p style={{ flex: 0.9 }}>
                                  {orderDetail?.payment_info?.amt ??
                                    orderDetail?.payment_info?.totalAmount}
                                </p>
                              </Flex>
                              <Flex>
                                <p style={{ flex: 0.1, fontWeight: 700 }}>
                                  결제카드:
                                </p>
                                <p style={{ flex: 0.9 }}>
                                  {orderDetail?.payment_info?.msg1 ??
                                    `${orderDetail?.payment_info?.purchaseCardType} ${orderDetail?.payment_info?.purchaseCardName}`}
                                </p>
                              </Flex>
                              <Flex>
                                <p style={{ flex: 0.1, fontWeight: 700 }}>
                                  할부:
                                </p>
                                <p style={{ flex: 0.9 }}>
                                  {orderDetail?.payment_info?.halbu
                                    ? orderDetail?.payment_info?.halbu === '00'
                                      ? '일시불'
                                      : orderDetail?.payment_info?.halbu
                                    : orderDetail?.payment_info
                                          ?.installMonth === '0'
                                      ? '일시불'
                                      : orderDetail?.payment_info?.installMonth}
                                </p>
                              </Flex>
                            </Flex>
                          ),
                        span: 2,
                      },
                    ]
                  : []),

                /* === Line 6 === */
                ...(type?.includes('RETURN') || type?.includes('EXCHANGE')
                  ? [
                      {
                        key: 'refund',
                        label: '환불사유',
                        children:
                          orderDetail?.parcel_service?.[0]?.refund?.[0]
                            ?.refund_reason,
                      },
                    ]
                  : []),
              ]}
            />
            <Flex
              align="center"
              justify="flex-end"
              gap={5}
              style={{ marginTop: 5 }}
            >
              {modalType === 'order' && type ? (
                <Button onClick={() => handleChangeDepositStatus()}>
                  입금확인
                </Button>
              ) : null}
              <Button
                color="danger"
                variant="outlined"
                onClick={() => {
                  const totalOrderPrice =
                    orderDetail?.product_information?.reduce(
                      (totalPrice, product) => {
                        const productPrice =
                          product.price * product.num +
                          // 여러 개 주문한 상품은 선택 옵션 가격도 상품 수량만큼 합산합니다.
                          (product?.product_option?.reduce(
                            (acc, cur) =>
                              acc +
                              cur?.option?.reduce(
                                (acc, cur) =>
                                  acc + (cur?.option_price || 0) * product.num,
                                0,
                              ),
                            0,
                          ) ?? 0);
                        return totalPrice + productPrice;
                      },
                      0,
                    );
                  const shop_order = {
                    use_point: orderDetail?.use_point ?? 0,
                    final_discount: orderDetail?.final_discount ?? 0,
                    final_delivery_price:
                      orderDetail?.final_delivery_price ?? 0,
                    total_order_price:
                      totalOrderPrice + orderDetail?.final_delivery_price,
                  };

                  setRefundModal(
                    modalType === 'order'
                      ? {
                          shop_order: shop_order,
                          parcel_service: orderDetail.parcel_service,
                        }
                      : {
                          shop_order: shop_order,
                          parcel_service: orderDetail.all_parcel_service,
                        },
                    // orderList.filter(
                    //   (item) => item.shop_order_id === orderDetail.shop_order_id
                    // )
                  );
                }}
              >
                {isDirectDepositRefundPending ? '환불대기' : '주문취소'}
              </Button>
            </Flex>
            {/* ===== 결제정보: end ===== */}
          </div>
        )}
      </Spin>
      {/* ===== 상세모달: start ===== */}
      <ModalLayout
        title={isDirectDepositRefundPending ? '환불대기' : '주문취소'}
        width={1500}
        open={refundModal !== null}
        closable={true}
        onCancel={() => {
          setRefundModal(null);
          setSelectedRefundList([]);
          setRefundPrice(0);
        }}
        footer={
          <>
            <Button
              color="danger"
              variant="outlined"
              onClick={handleRefund}
              disabled={selectedRefundList?.length === 0}
            >
              {/* 대기 중인 환불을 선택한 경우 실제 처리 목적을 명확히 표시합니다. */}
              {selectedRefundList?.some((item) =>
                item?.refund?.some(
                  (refund) => refund?.refund_status === 'PENDING',
                ),
              )
                ? '환불완료 처리'
                : '주문취소'}
            </Button>
          </>
        }
      >
        <Divider style={{ margin: '12px 0' }} />
        {refundModal && (
          <>
            <p>주문건 결제 정보</p>
            <Descriptions
              column={5}
              size="small"
              bordered
              labelStyle={{
                width: 125,
                color: '#111',
                fontWeight: '500',
                background: '#f4f4f4',
              }}
              style={{
                borderTop: '2px solid #d1d1d1',
                borderRadius: 5,
                marginTop: 5,
              }}
              items={[
                {
                  key: '1',
                  label: '총 주문금액',
                  children: (
                    <p>
                      {stringToMoneyFormat(
                        refundModal?.shop_order?.total_order_price,
                        '원',
                      )}
                    </p>
                  ),
                },
                {
                  key: '1',
                  label: '총 상품결제금액',
                  children: (
                    <p>
                      {stringToMoneyFormat(
                        refundModal?.parcel_service?.reduce(
                          (acc, cur) => (acc += cur?.payment_price ?? 0),
                          0,
                        ),
                      )}
                      원
                    </p>
                  ),
                },
                {
                  key: '2',
                  label: '배송비',
                  children: (
                    <p>
                      {stringToMoneyFormat(
                        refundModal?.shop_order?.final_delivery_price,
                      )}
                      원
                    </p>
                  ),
                },
                {
                  key: '3',
                  label: '총 쿠폰/할인',
                  children: (
                    <p>
                      {stringToMoneyFormat(
                        refundModal?.shop_order?.final_discount,
                        '원',
                      )}
                    </p>
                  ),
                },
                {
                  key: '4',
                  label: '총 사용포인트',
                  children: (
                    <p>
                      {stringToMoneyFormat(refundModal?.shop_order?.use_point)}
                      원
                    </p>
                  ),
                },
                {
                  key: '5',
                  label: '총결제금액',
                  children: (
                    <p>
                      {stringToMoneyFormat(
                        refundModal?.parcel_service?.reduce(
                          (acc, cur) => (acc += cur?.payment_price ?? 0),
                          0,
                        ) + refundModal?.shop_order?.final_delivery_price,
                      )}
                      원
                    </p>
                  ),
                },
              ]}
            />
            <br />
            <p>환불 정보</p>
            <Table
              size="small"
              dataSource={refundModal?.parcel_service?.filter(
                (parcel) => parcel?.refund?.length > 0,
              )}
              style={{
                borderTop: '2px solid #d1d1d1',
                borderRadius: 5,
                marginTop: 5,
              }}
              columns={[
                {
                  title: '상품명',
                  dataIndex: 'product_name',
                  width: 250,
                  render: (_, item) => item?.product?.product_name,
                },
                {
                  title: '환불금액',
                  dataIndex: 'refund_amount',
                  width: 100,
                  render: (_, item) =>
                    getLatestRefund(item?.refund)?.refund_amount,
                },
                {
                  title: '환불수량',
                  dataIndex: 'refund_num',
                  width: 80,
                  render: (_, item) =>
                    getLatestRefund(item?.refund)?.refund_num,
                },
                {
                  title: '환불상태',
                  dataIndex: 'refund_status',
                  width: 100,
                  render: (_, item) => {
                    // 최신 환불 상태를 기준으로 관리자 화면에 표시합니다.
                    const refundStatus = getLatestRefund(
                      item?.refund,
                    )?.refund_status;

                    return (
                      <Tag color={REFUND_STATUS[refundStatus]?.color}>
                        {REFUND_STATUS[refundStatus]?.label}
                      </Tag>
                    );
                  },
                },
                {
                  title: '환불사유',
                  dataIndex: 'refund_reason',
                  width: 200,
                  render: (_, item) =>
                    getLatestRefund(item?.refund)?.refund_reason,
                },
                {
                  title: '환불일자',
                  dataIndex: 'refund_date',
                  width: 250,
                  render: (_, item) =>
                    dayjs
                      .unix(getLatestRefund(item?.refund)?.refund_date)
                      .format('YYYY-MM-DD HH:mm:ss'),
                },
                {
                  title: '환불데이터',
                  dataIndex: 'refund_data',
                  width: 250,
                  render: (_, item) => (
                    <ul style={{ fontSize: 12 }}>
                      {(() => {
                        const couponInfo =
                          item?.refund[0]?.refund_data?.coupon_info;
                        const currentCouponUserId =
                          item?.coupon_info?.coupon_user_id;
                        const couponName =
                          item?.coupon_info?.policy_coupon?.coupon_name;

                        if (!couponInfo) return null;

                        if (Array.isArray(couponInfo)) {
                          return couponInfo.map((ReCoupon, idx) =>
                            ReCoupon?.coupon_user_id === currentCouponUserId ? (
                              <li key={idx}>복구쿠폰 : {couponName}</li>
                            ) : null,
                          );
                        }

                        if (
                          typeof couponInfo === 'object' &&
                          couponInfo?.coupon_user_id === currentCouponUserId
                        ) {
                          return <li>복구쿠폰 : {couponName}</li>;
                        }

                        return null;
                      })()}
                      {/* 포인트 */}
                      {(() => {
                        const pointHistory =
                          item?.refund[0]?.refund_data?.point_history;

                        if (Array.isArray(pointHistory)) {
                          const totalPoint = pointHistory.reduce(
                            (acc, cur) => acc + (cur?.point || 0),
                            0,
                          );
                          return <li>복구포인트 : {totalPoint} P</li>;
                        } else if (
                          pointHistory &&
                          typeof pointHistory === 'object'
                        ) {
                          return (
                            <li>복구포인트 : {pointHistory?.point || 0} P</li>
                          );
                        } else {
                          return null;
                        }
                      })()}
                    </ul>
                  ),
                },
                {
                  title: '환불결제정보',
                  dataIndex: 'refund_payment_info',
                  width: 250,
                  render: (_, item) =>
                    item?.refund[0]?.refund_payment_info ? (
                      <ul style={{ fontSize: 12 }}>
                        <li>
                          - 취소 승인번호:{' '}
                          {item?.refund[0]?.refund_payment_info?.tid}{' '}
                        </li>
                        <li>
                          - 취소 금액:{' '}
                          {
                            item?.refund[0]?.refund_payment_info?.cancelAmount
                          }{' '}
                        </li>
                        <li>
                          - 카드사:{' '}
                          {
                            item?.refund[0]?.refund_payment_info?.issuerCardName
                          }{' '}
                        </li>
                        <li>
                          - 취소시간:{' '}
                          {item?.refund[0]?.refund_payment_info?.tradeDateTime.slice(
                            0,
                            8,
                          )}
                          {'-'}
                          {item?.refund[0]?.refund_payment_info?.tradeDateTime.slice(
                            8,
                            14,
                          )}
                        </li>
                      </ul>
                    ) : (
                      <>{'-'}</>
                    ),
                },
                {
                  title: '환불현금영수증정보',
                  dataIndex: 'refund_cash_bill_info',
                  width: 250,
                  render: (_, item) =>
                    item?.refund[0]?.refund_cash_bill_info ? (
                      <ul style={{ fontSize: 12 }}>
                        <li>
                          - 취소 승인번호:{' '}
                          {item?.refund[0]?.refund_cash_bill_info?.tid}{' '}
                        </li>
                        <li>
                          - 취소 금액:{' '}
                          {
                            item?.refund[0]?.refund_cash_bill_info?.cancelAmount
                          }{' '}
                        </li>
                        <li>
                          - 메세지:{' '}
                          {
                            item?.refund[0]?.refund_cash_bill_info?.respMessage?.split(
                              '/',
                            )[0]
                          }{' '}
                        </li>
                        <li>
                          - 취소시간:{' '}
                          {item?.refund[0]?.refund_cash_bill_info?.tradeDateTime.slice(
                            0,
                            8,
                          )}
                          {'-'}
                          {item?.refund[0]?.refund_cash_bill_info?.tradeDateTime.slice(
                            8,
                            14,
                          )}
                        </li>
                      </ul>
                    ) : (
                      <>{'-'}</>
                    ),
                },
              ]}
              pagination={false}
              scroll={{ y: 480, x: 'max-content' }} // 테이블 자체 스크롤 설정
            ></Table>
            <br />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <p>취소항목을 선택해주세요</p>
              <p>[{selectedRefundList.length}]개 상품 선택</p>
            </div>
            <Table
              size="small"
              dataSource={refundModal?.parcel_service
                // ?.filter(
                //   (item) =>
                //     !item?.refund?.length ||
                //     (item?.refund?.length &&
                //       !item?.refund?.some(
                //         (i) => i?.refund_status === 'COMPLETED'
                //       ))
                // )
                // ?.filter((item) => item.purchase_status !== 'REFUNDED')
                ?.map((item) => ({
                  ...item,
                  key: item.parcel_service_id,
                  total_order_price: refundModal?.shop_order?.total_order_price,
                }))}
              pagination={false}
              scroll={{ y: 480, x: 'max-content' }} // 테이블 자체 스크롤 설정
              style={{
                marginTop: 5,
                borderTop: '2px solid #d1d1d1',
                borderRadius: 5,
              }}
              rowSelection={{
                type: 'radio',
                selectedRowKeys: selectedRefundList?.map((item) => item.key),

                onChange: (_, row) => {
                  // 기존 환불대기 건은 최초 요청의 금액과 사유를 그대로 사용합니다.
                  const pendingRefund = row?.[0]?.refund?.find(
                    (refund) => refund?.refund_status === 'PENDING',
                  );

                  setSelectedRefundList(row);
                  setRefundPrice(
                    pendingRefund?.refund_amount ??
                      row.reduce((acc, cur) => (acc += cur.payment_price), 0),
                  );
                  setRefundReason(pendingRefund?.refund_reason ?? '');
                  setRecoveryValue((prev) => ({
                    ...prev,
                    is_coupon_refund: 'Y',
                    refund_point: row?.reduce(
                      (acc, cur) =>
                        (acc += Math.abs(cur.product?.use_point || 0)),
                      0,
                    ),
                  }));
                },
                getCheckboxProps: (record) => ({
                  // 최신 환불이 완료된 상품만 추가 환불 처리를 막습니다.
                  disabled:
                    getLatestRefund(record?.refund)?.refund_status ===
                    'COMPLETED',
                }),
              }}
              // isChecked
              bordered
              columns={[
                {
                  title: '상품코드',
                  dataIndex: 'product',
                  width: 135,
                  render: (p) => p.product_code,
                },
                {
                  title: '상품명',
                  dataIndex: 'product',
                  width: 250,
                  render: (p) => p.product_name,
                },
                {
                  title: '수량',
                  dataIndex: 'num',
                  width: 70,
                  render: (p) => stringToMoneyFormat(p),
                },
                {
                  title: '단가',
                  dataIndex: 'product',
                  width: 100,
                  render: (p) => stringToMoneyFormat(p.price),
                },
                {
                  title: '결제금액',
                  dataIndex: 'payment_price',
                  width: 110,
                  render: (p) => stringToMoneyFormat(p),
                },
                {
                  title: '사용포인트(상품당)',
                  dataIndex: 'product',
                  width: 140,
                  render: (product) =>
                    `${stringToMoneyFormat(product?.use_point)}P`,
                },
                {
                  title: '적용쿠폰',
                  dataIndex: 'coupon_info',
                  width: 200,
                  render: (coupon_info) =>
                    coupon_info ? (
                      <ul style={{ fontSize: 12 }}>
                        <li>
                          - 쿠폰명: {coupon_info?.policy_coupon?.coupon_name}
                        </li>
                        <li>- 할인: {coupon_info?.discount_price}원</li>
                        <li>- 적립: {coupon_info?.reserve_price}P</li>
                      </ul>
                    ) : (
                      <></>
                    ),
                },
                {
                  title: '결제상태',
                  dataIndex: 'purchase_status',
                  width: 100,
                  render: (p) => (
                    <Tag color={PURCHASE_STATUS[p]?.color}>
                      {PURCHASE_STATUS[p]?.title}
                    </Tag>
                  ),
                },
                {
                  title: '환불금액',
                  dataIndex: 'refund',
                  width: 100,
                  render: (refund) =>
                    stringToMoneyFormat(
                      refund?.reduce(
                        (acc, cur) => (acc += cur?.refund_amount ?? 0),
                        0,
                      ),
                    ),
                },
                {
                  title: '포장방식',
                  dataIndex: 'product',
                  width: 100,
                  render: (p) => PACKAGING_METHOD[p.packaging_method]?.title,
                },
              ]}
            />
            <br />
            <Flex vertical gap={10}>
              <Title size={5}>취소정보</Title>
              <Flex gap={3} style={{ alignItems: 'center' }}>
                <p>환불금액: </p>
                <InputNumber
                  placeholder="환불 금액을 입력해주세요"
                  min={0}
                  max={selectedRefundList.reduce(
                    (acc, cur) => (acc += cur.total_order_price),
                    0,
                  )}
                  value={refundPrice}
                  onChange={(e) => setRefundPrice(e)}
                  disabled={selectedRefundList.length <= 0}
                  style={{ width: '80%' }}
                />
              </Flex>
              <Flex gap={3} style={{ alignItems: 'center' }}>
                <p>환불사유: </p>
                <Input.TextArea
                  placeholder="텍스트를 입력해주세요"
                  rows={2}
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  disabled={selectedRefundList.length <= 0}
                  style={{ width: '80%' }}
                />
              </Flex>
              <Flex gap={3} style={{ alignItems: 'center' }}>
                <p>복구포인트: </p>
                <InputNumber
                  placeholder="복구될 포인트를 입력하세요"
                  min={0}
                  value={recoveryValue?.refund_point}
                  onChange={(e) =>
                    setRecoveryValue((prev) => ({
                      ...prev,
                      refund_point: e,
                    }))
                  }
                  disabled={selectedRefundList.length <= 0}
                  style={{ width: '80%' }}
                />
              </Flex>
              <Flex gap={3} style={{ alignItems: 'center' }}>
                <p>쿠폰복구여부: </p>
                <Radio.Group
                  disabled={selectedRefundList.length <= 0}
                  value={recoveryValue.is_coupon_refund}
                  onChange={(e) => {
                    setRecoveryValue((prev) => ({
                      ...prev,
                      is_coupon_refund: e.target.value,
                    }));
                  }}
                >
                  <Radio value="Y">쿠폰복구</Radio>
                  <Radio value="N">쿠폰복구안함</Radio>
                </Radio.Group>
              </Flex>
            </Flex>
          </>
        )}
      </ModalLayout>
      {/* ===== 상세모달: end ===== */}
    </>
  ); // JSX: end
};

OrderDetail.defaultProps = {
  orderId: null,
  isModalVisible: null,
  orderList: [],
  onLoaded: () => {},
  onUpdateParcel: () => {},
};
export default OrderDetail;
